import { parseStreamCursor } from 'tripPlanner/utils/streamCursor'
import { isRecord } from 'lib/object/objectUtils'
import { STREAM_EVENT } from 'tripPlanner/api/types'
import { parseStreamEvent, type StreamEvent } from 'tripPlanner/utils/streamEventParser'

const RECOVERABLE_EVENTS: ReadonlySet<string> = new Set([
  STREAM_EVENT.PARTS_UPDATE,
  STREAM_EVENT.MESSAGE_END,
  STREAM_EVENT.TOKEN_BATCH,
  STREAM_EVENT.UI_PART_STARTED,
  STREAM_EVENT.UI_PART_PATCH,
  STREAM_EVENT.UI_PART_COMPLETED,
  STREAM_EVENT.UI_PART_FAILED,
  STREAM_EVENT.STATUS,
  STREAM_EVENT.CHAT_LOADING_STATE,
  STREAM_EVENT.ITINERARY_UPDATE_STARTED,
  STREAM_EVENT.ITINERARY_UPDATES,
  STREAM_EVENT.CONTENT_COMPLETE,
])

type CatchUp =
  | { kind: 'structured'; events: Array<StreamEvent> }
  | { kind: 'legacy'; text: string }

export function parseStreamRecovery(raw: string, messageId: string, cursor: string): CatchUp {
  const identity = parseStreamCursor(cursor)
  if (!identity) throw new Error('Invalid recovery cursor')
  const payload: unknown = JSON.parse(raw)
  if (!isRecord(payload)) throw new Error('Invalid stream recovery payload')
  if (!('recovery' in payload)) {
    if (identity.protocol === 'attempt_v1') throw new Error('Attempt recovery checkpoint missing')
    if (typeof payload.assembledContent !== 'string') throw new Error('Invalid legacy stream recovery')
    return { kind: 'legacy', text: payload.assembledContent }
  }
  const { recovery } = payload
  if (
    !isRecord(recovery) || recovery.messageId !== messageId || recovery.cursor !== cursor ||
    identity.cursor === '0' || !Array.isArray(recovery.events) ||
    (identity.protocol === 'legacy' ? recovery.version !== 1 :
      recovery.version !== 2 || identity.messageId !== messageId || identity.generationStreamId !== recovery.generationStreamId)
  ) throw new Error('Invalid stream recovery checkpoint')

  const events: Array<StreamEvent> = []
  for (const rawEvent of recovery.events) {
    const event = typeof rawEvent === 'string' ? parseStreamEvent(rawEvent) : undefined
    if (
      !event || !RECOVERABLE_EVENTS.has(event.event) ||
      ('messageId' in event && event.messageId !== messageId)
    ) throw new Error('Invalid stream recovery event')
    if (
      (event.event === STREAM_EVENT.PARTS_UPDATE || event.event === STREAM_EVENT.MESSAGE_END) &&
      event.parts.some(part => part.type === 'text' && typeof part.content !== 'string')
    ) throw new Error('Invalid stream recovery text part')
    events.push(event)
  }
  return { kind: 'structured', events }
}
